// Vendor Program v2 PR 3 — the §4 audit drawer for a draft VFI invoice
// (deep-linked ?draft=<id>). The five-second audit: draft amount large,
// the source report's prior accepted amounts + amount-calculation
// explanation (when report-sourced), and the vendor's billing terms.
// Issue / Void act via the shared confirm modals.
//
// NOTE: billingTermsDocumentId renders as plain "Terms document on file"
// text — the platform has no /documents/:id viewer route to link to.

import { formatPeriodMonth } from "@/design-system/lib/format";
import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { useVendorInvoice } from "@/hooks/portfolioAdmin/useVendorBilling";
import { useVendorReportReview } from "@/hooks/portfolioAdmin/useVendorReports";
import { useVendor } from "@/hooks/portfolioAdmin/useRegistries";
import { fmtMoney } from "../billing/vendorBillingFormat";
import { ContractCheckPanel } from "./ContractCheckPanel";
import { IssueDraftsConfirmModal } from "./IssueDraftsConfirmModal";
import { VoidDraftModal } from "./VoidDraftModal";

interface DraftInvoiceAuditDrawerProps {
  invoiceId: string;
  open: boolean;
  onClose: () => void;
  onOpenReport: (reportId: string) => void;
}

export function DraftInvoiceAuditDrawer({
  invoiceId,
  open,
  onClose,
  onOpenReport,
}: DraftInvoiceAuditDrawerProps) {
  const auth = useAuth();

  const detailQuery = useVendorInvoice(invoiceId);
  const invoice = detailQuery.data?.invoice;
  const sourceReportId = invoice?.sourceReportId ?? undefined;

  const reviewQuery = useVendorReportReview(sourceReportId);
  const vendorQuery = useVendor(invoice?.vendorId);

  const [modal, setModal] = useState<null | "issue" | "void">(null);

  const canIssue =
    auth.hasPermission("VendorBilling.Issue") || auth.hasRoleCode("ADMIN");
  const canVoid = auth.hasPermission("VendorBilling.Void");
  const isDraft = invoice?.status === "draft";

  // Period boundaries are wall-calendar months stored at UTC midnight —
  // rendering them through the tz-aware formatter shifted June into May.
  const month = (iso: string) => formatPeriodMonth(iso, "short");

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={invoice ? invoice.invoiceNumber : "Draft invoice"}
      subtitle={
        invoice ? `${invoice.vendorName} · ${invoice.loanNumber}` : undefined
      }
      testId="vfi-draft-audit-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {invoice && isDraft && canVoid && (
            <Button
              variant="destructive"
              onClick={() => setModal("void")}
              data-testid="audit-void-button"
            >
              Void
            </Button>
          )}
          {invoice && isDraft && canIssue && (
            <Button
              variant="primary"
              onClick={() => setModal("issue")}
              data-testid="audit-issue-button"
            >
              Issue
            </Button>
          )}
        </>
      }
    >
      {detailQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading invoice…</p>
      )}
      {detailQuery.isError && (
        <p className="text-small text-status-error m-0" role="alert">
          Could not load the invoice. Refresh to retry.
        </p>
      )}
      {invoice && (
        <div className="flex flex-col gap-space-4">
          <section data-testid="audit-amount">
            <span className="block text-caption text-gray-mid uppercase tracking-label">
              Draft amount
            </span>
            <span className="block font-display font-bold text-h1 tabular-nums text-right">
              {fmtMoney(invoice.amount)}
            </span>
            {invoice.description && (
              <p className="text-small text-gray-mid m-0 mt-space-1 whitespace-pre-wrap">
                {invoice.description}
              </p>
            )}
          </section>

          {/* SP-4 WS-2 — the contract check, computed live server-side. */}
          <section data-testid="audit-contract-check">
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Contract check
            </h3>
            <ContractCheckPanel
              comparison={invoice.contractComparison}
              vendorId={invoice.vendorId}
            />
          </section>

          <section data-testid="audit-source">
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Source
            </h3>
            {!sourceReportId && (
              <p className="text-small text-gray-mid m-0">
                Manual invoice — not generated from a monthly vendor report.
              </p>
            )}
            {sourceReportId && (
              <>
                <button
                  type="button"
                  className="text-small text-gold-shadow underline hover:text-onyx"
                  onClick={() => onOpenReport(sourceReportId)}
                  data-testid="audit-open-report"
                >
                  Open the source report review
                </button>
                {reviewQuery.isLoading && (
                  <p className="text-small text-gray-mid italic m-0 mt-space-2">
                    Loading the source report…
                  </p>
                )}
                {reviewQuery.isError && (
                  <p className="text-small text-status-error m-0 mt-space-2">
                    Could not load the source report.
                  </p>
                )}
                {reviewQuery.data && (
                  <div className="mt-space-2 flex flex-col gap-space-2">
                    {reviewQuery.data.report.amountCalculationExplanation && (
                      <div data-testid="audit-amount-explanation">
                        <span className="text-caption text-gray-mid uppercase tracking-label">
                          §4 Amount calculation explanation
                        </span>
                        <p className="text-small m-0 mt-space-1 whitespace-pre-wrap">
                          {reviewQuery.data.report.amountCalculationExplanation}
                        </p>
                      </div>
                    )}
                    <div data-testid="audit-prior-amounts">
                      <span className="text-caption text-gray-mid uppercase tracking-label">
                        Prior accepted amounts
                      </span>
                      {reviewQuery.data.priorAmounts.length === 0 ? (
                        <p className="text-small text-gray-mid italic m-0 mt-space-1">
                          No prior accepted reports for this assignment.
                        </p>
                      ) : (
                        <ul className="m-0 mt-space-1 p-0 list-none flex flex-col gap-space-1">
                          {reviewQuery.data.priorAmounts.map((p) => (
                            <li
                              key={p.periodStart}
                              className="flex items-baseline justify-between gap-space-2 text-small border-b border-border-light pb-space-1"
                            >
                              <span>{month(p.periodStart)}</span>
                              <span className="text-right tabular-nums">
                                {p.amount != null ? fmtMoney(p.amount) : "—"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          <section data-testid="audit-billing-terms">
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Vendor billing terms
            </h3>
            {vendorQuery.isLoading && (
              <p className="text-small text-gray-mid italic m-0">
                Loading vendor…
              </p>
            )}
            {vendorQuery.isError && (
              <p className="text-small text-status-error m-0">
                Could not load the vendor record.
              </p>
            )}
            {vendorQuery.data && (
              <>
                {vendorQuery.data.billingTermsNotes ? (
                  <p className="text-small m-0 whitespace-pre-wrap">
                    {vendorQuery.data.billingTermsNotes}
                  </p>
                ) : (
                  <p className="text-small text-gray-mid italic m-0">
                    No billing terms recorded — edit the vendor record to add
                    them.
                  </p>
                )}
                {vendorQuery.data.billingTermsDocumentId && (
                  <p
                    className="text-caption text-gray-mid m-0 mt-space-1"
                    data-testid="audit-terms-document"
                  >
                    Terms document on file.
                  </p>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {invoice && modal === "issue" && (
        <IssueDraftsConfirmModal
          invoices={[invoice]}
          open
          onClose={() => setModal(null)}
          onIssued={(r) => {
            if (r.issued.length > 0) onClose();
          }}
        />
      )}
      {invoice && modal === "void" && (
        <VoidDraftModal
          invoice={invoice}
          open
          onClose={() => setModal(null)}
        />
      )}
    </ResourceDrawer>
  );
}
